import React, { useEffect, useRef, useState } from 'react';
import { howItWorksTabs } from '../mock';
import IsometricStore from './IsometricStore';

/**
 * StoreIllustration — a detailed halftone pen-drawing style SVG of an
 * Asian mini-market / grocery store interior. Progress (0..1) drives
 * layered reveal of elements. Active key highlights the current focus.
 */
const StoreIllustration = ({ activeKey, progress = 0 }) => {
  const isDemand = activeKey === 'demand';
  const isLabor = activeKey === 'labor';
  const isInventory = activeKey === 'inventory';
  const isLocations = activeKey === 'locations';

  const accent = '#2E6BFF';
  const ink = '#111';
  const dimmed = 'rgba(17,17,17,0.55)';

  // Layer opacities based on scroll progress within the section
  const layerA = Math.max(0, Math.min(1, progress * 4)); // frame + ceiling
  const layerB = Math.max(0, Math.min(1, (progress - 0.15) * 4)); // aisles
  const layerC = Math.max(0, Math.min(1, (progress - 0.35) * 4)); // produce + freezer
  const layerD = Math.max(0, Math.min(1, (progress - 0.55) * 4)); // checkout + staff
  const layerE = Math.max(0, Math.min(1, (progress - 0.75) * 4)); // customers + locations map

  return (
    <div className="illustration aspect-[16/11] w-full">
      <svg viewBox="0 0 960 620" className="w-full h-full block">
        <defs>
          <pattern id="dots-fine" x="0" y="0" width="4" height="4" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.6" fill="rgba(0,0,0,0.35)" />
          </pattern>
          <pattern id="dots-mid" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.9" fill="rgba(0,0,0,0.55)" />
          </pattern>
          <pattern id="stripes" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="2" height="6" fill="rgba(0,0,0,0.35)" />
          </pattern>
        </defs>

        {/* Paper */}
        <rect width="100%" height="100%" fill="#f5f4ee" />
        <rect width="100%" height="100%" fill="url(#dots-fine)" opacity="0.35" />

        {/* LAYER A — perimeter frame + ceiling */}
        <g opacity={layerA} style={{ transition: 'opacity 260ms ease' }}>
          <g stroke={ink} strokeWidth="1.4" fill="none">
            <line x1="60" y1="70" x2="900" y2="70" />
            <line x1="60" y1="70" x2="60" y2="560" />
            <line x1="900" y1="70" x2="900" y2="560" />
            <line x1="40" y1="560" x2="920" y2="560" />
            <g opacity="0.35">
              {Array.from({ length: 12 }).map((_, i) => (
                <line key={i} x1={40 + i * 75} y1="560" x2={80 + i * 75} y2="600" />
              ))}
            </g>
          </g>
          {/* Signage strip */}
          <rect x="90" y="82" width="780" height="46" fill="none" stroke={ink} strokeWidth="1.2" />
          <rect x="94" y="86" width="772" height="38" fill="url(#dots-mid)" opacity={isDemand ? 0.15 : 0.25} />
          {['01', '02', '03', '04', '05', '06'].map((n, i) => (
            <g key={n}>
              <rect x={110 + i * 128} y="90" width="72" height="30" fill="#f5f4ee" stroke={ink} strokeWidth="1" />
              <text
                x={146 + i * 128}
                y="110"
                fontFamily="JetBrains Mono, monospace"
                fontSize="12"
                textAnchor="middle"
                fill={isDemand && i === 3 ? accent : ink}
              >
                AISLE {n}
              </text>
            </g>
          ))}
          {/* Pendant lights */}
          <g stroke={isDemand ? accent : ink} strokeWidth="1.2" fill={isDemand ? accent : '#f5f4ee'}>
            {[180, 340, 500, 660, 820].map((x) => (
              <g key={x}>
                <line x1={x} y1="128" x2={x} y2="150" stroke={ink} strokeWidth="0.8" />
                <path d={`M${x - 12} 168 L${x + 12} 168 L${x + 8} 152 L${x - 8} 152 Z`} />
              </g>
            ))}
          </g>
        </g>

        {/* Weather/demand signal chip */}
        {isDemand && layerA > 0.5 && (
          <g opacity={layerA}>
            <rect x="80" y="180" width="150" height="46" fill="#f5f4ee" stroke={accent} strokeWidth="1.4" />
            <text x="92" y="200" fontFamily="JetBrains Mono, monospace" fontSize="10" fill={accent}>
              WED · RAIN
            </text>
            <text x="92" y="216" fontFamily="JetBrains Mono, monospace" fontSize="10" fill={ink}>
              +14% BASKETS 17:00
            </text>
          </g>
        )}

        {/* LAYER B — aisles */}
        <g opacity={layerB} style={{ transition: 'opacity 260ms ease' }}>
          <g stroke={isInventory ? accent : ink} strokeWidth={isInventory ? 1.6 : 1.2} fill="none">
            <rect x="110" y="270" width="150" height="230" />
            <rect x="110" y="270" width="150" height="230" fill="url(#dots-mid)" opacity={isInventory ? 0.28 : 0.4} />
            {[310, 350, 390, 430, 470].map((y) => (
              <line key={y} x1="110" y1={y} x2="260" y2={y} />
            ))}
            <rect x="118" y="303" width="24" height="10" fill="#f5f4ee" />
            <rect x="200" y="343" width="24" height="10" fill="#f5f4ee" />
            <rect x="148" y="423" width="24" height="10" fill="#f5f4ee" />
          </g>
          <g stroke={isInventory ? accent : ink} strokeWidth={isInventory ? 1.6 : 1.2} fill="none">
            <rect x="300" y="270" width="150" height="230" />
            <rect x="300" y="270" width="150" height="230" fill="url(#dots-mid)" opacity={isInventory ? 0.28 : 0.4} />
            {[310, 350, 390, 430, 470].map((y) => (
              <line key={y} x1="300" y1={y} x2="450" y2={y} />
            ))}
            {[[318, 314], [340, 314], [362, 314], [384, 314], [406, 314], [428, 314]].map(([cx2, cy2], i) => (
              <circle key={i} cx={cx2} cy={cy2} r="6" stroke={ink} fill={isInventory && i < 4 ? accent : '#f5f4ee'} strokeWidth="1" opacity={isInventory && i < 4 ? 1 : 0.7} />
            ))}
            {isInventory && (
              <rect x="300" y="430" width="150" height="40" fill="none" stroke={accent} strokeDasharray="3 3" strokeWidth="1.4" />
            )}
          </g>
        </g>

        {/* LAYER C — produce + freezer */}
        <g opacity={layerC} style={{ transition: 'opacity 260ms ease' }}>
          <g stroke={ink} strokeWidth="1.2" fill="none">
            <rect x="490" y="330" width="170" height="150" />
            <rect x="490" y="330" width="170" height="150" fill="url(#stripes)" opacity="0.25" />
            <g stroke={ink} strokeWidth="1">
              <circle cx="520" cy="360" r="10" fill="#f5f4ee" />
              <circle cx="540" cy="358" r="8" fill="#f5f4ee" />
              <circle cx="558" cy="362" r="10" fill="#f5f4ee" />
              <circle cx="600" cy="360" r="12" fill="#f5f4ee" />
              <circle cx="628" cy="362" r="9" fill="#f5f4ee" />
              <ellipse cx="520" cy="420" rx="14" ry="7" fill="#f5f4ee" />
              <ellipse cx="580" cy="420" rx="14" ry="7" fill="#f5f4ee" />
              <ellipse cx="628" cy="420" rx="14" ry="7" fill="#f5f4ee" />
            </g>
            <text x="497" y="345" fontFamily="JetBrains Mono, monospace" fontSize="9" fill={ink}>
              FRESH PRODUCE
            </text>
          </g>

          {/* Cold storage */}
          <g stroke={ink} strokeWidth="1.2" fill="none">
            <rect x="700" y="240" width="160" height="260" />
            <rect x="700" y="240" width="160" height="260" fill="url(#dots-mid)" opacity="0.35" />
            <line x1="780" y1="240" x2="780" y2="500" />
            <rect x="708" y="250" width="64" height="240" stroke={ink} strokeWidth="0.8" fill="none" />
            <rect x="788" y="250" width="64" height="240" stroke={ink} strokeWidth="0.8" fill="none" />
            <line x1="712" y1="360" x2="712" y2="380" strokeWidth="2" />
            <line x1="848" y1="360" x2="848" y2="380" strokeWidth="2" />
            <rect x="712" y="256" width="56" height="14" fill="#f5f4ee" />
            <text x="716" y="266" fontFamily="JetBrains Mono, monospace" fontSize="9" fill={ink}>
              -18°C
            </text>
            <rect x="792" y="256" width="56" height="14" fill="#f5f4ee" />
            <text x="796" y="266" fontFamily="JetBrains Mono, monospace" fontSize="9" fill={ink}>
              +2°C
            </text>
          </g>
        </g>

        {/* LAYER D — checkout */}
        <g opacity={layerD} style={{ transition: 'opacity 260ms ease' }}>
          <g stroke={isLabor ? accent : ink} strokeWidth={isLabor ? 1.6 : 1.2} fill="none">
            <rect x="110" y="500" width="470" height="30" fill="#f5f4ee" />
            <line x1="230" y1="500" x2="230" y2="530" />
            <line x1="350" y1="500" x2="350" y2="530" />
            <line x1="470" y1="500" x2="470" y2="530" />
            {[170, 290, 410, 530].map((x, i) => (
              <g key={x}>
                <rect x={x - 18} y="482" width="36" height="20" fill="#f5f4ee" />
                <rect x={x - 14} y="486" width="28" height="12" fill={isLabor && (i === 0 || i === 2) ? accent : 'url(#dots-mid)'} opacity={isLabor && (i === 0 || i === 2) ? 0.9 : 0.6} />
              </g>
            ))}
            {[170, 290, 410, 530].map((x, i) => (
              <g key={`c-${x}`} stroke={ink} strokeWidth="1">
                <circle cx={x} cy="466" r="10" fill={isLabor && (i === 0 || i === 2) ? accent : '#f5f4ee'} />
                <path d={`M${x - 10} 460 Q${x} 452 ${x + 10} 460`} fill={isLabor && (i === 0 || i === 2) ? accent : '#f5f4ee'} />
                <path d={`M${x - 12} 500 L${x - 12} 480 Q${x} 472 ${x + 12} 480 L${x + 12} 500`} fill="#f5f4ee" />
              </g>
            ))}
          </g>
          {isLabor && (
            <g stroke={accent} strokeWidth="1.4" fill="none">
              <rect x="472" y="480" width="76" height="52" strokeDasharray="4 3" />
            </g>
          )}
        </g>

        {/* LAYER E — customers + locations */}
        <g opacity={layerE} style={{ transition: 'opacity 260ms ease' }}>
          <g stroke={ink} strokeWidth="1" fill="none">
            <circle cx="170" cy="548" r="7" fill={isLocations ? accent : '#f5f4ee'} />
            <path d="M162 566 L162 555 Q170 550 178 555 L178 566" fill="#f5f4ee" />
            <rect x="150" y="568" width="26" height="14" />
            <circle cx="154" cy="586" r="2.5" fill={ink} />
            <circle cx="172" cy="586" r="2.5" fill={ink} />

            <circle cx="290" cy="548" r="7" fill="#f5f4ee" />
            <path d="M282 566 L282 555 Q290 550 298 555 L298 566" fill="#f5f4ee" />

            <circle cx="405" cy="548" r="7" fill={isLocations ? accent : '#f5f4ee'} />
            <path d="M397 566 L397 555 Q405 550 413 555 L413 566" fill="#f5f4ee" />
            <circle cx="422" cy="554" r="5" fill="#f5f4ee" />
            <path d="M417 566 L417 558 Q422 555 427 558 L427 566" fill="#f5f4ee" />

            <circle cx="620" cy="540" r="8" fill="#f5f4ee" />
            <path d="M610 560 L610 548 Q620 542 630 548 L630 560" fill="#f5f4ee" />
            <rect x="595" y="562" width="46" height="20" />
            <rect x="595" y="562" width="46" height="20" fill="url(#dots-mid)" opacity="0.6" />
            <circle cx="602" cy="586" r="3" fill={ink} />
            <circle cx="634" cy="586" r="3" fill={ink} />

            <g>
              <circle cx="270" cy="255" r="8" fill={isLabor ? accent : '#f5f4ee'} />
              <path d={`M262 275 L262 262 Q270 256 278 262 L278 275`} fill="#f5f4ee" />
              <rect x="255" y="278" width="30" height="14" />
              <circle cx="260" cy="296" r="2.5" fill={ink} />
              <circle cx="280" cy="296" r="2.5" fill={ink} />
            </g>
          </g>

          {isLocations && (
            <g>
              <rect x="710" y="90" width="160" height="90" fill="#f5f4ee" stroke={accent} strokeWidth="1.4" />
              <text x="720" y="108" fontFamily="JetBrains Mono, monospace" fontSize="10" fill={accent}>
                12 STORES · LIVE
              </text>
              {[
                [730, 130], [760, 122], [790, 138], [820, 128], [850, 140],
                [740, 156], [770, 148], [800, 162], [830, 152], [860, 168],
              ].map(([cxx, cyy], i) => (
                <circle key={i} cx={cxx} cy={cyy} r={i === 3 ? 4 : 2.5} fill={i === 3 ? accent : ink} />
              ))}
              <path d="M730 130 L760 122 L790 138 L820 128 L850 140" stroke={accent} strokeWidth="0.8" fill="none" opacity="0.6" />
            </g>
          )}

          <g stroke={ink} strokeWidth="1.2" fill="none">
            <line x1="880" y1="500" x2="880" y2="560" />
            <path d="M880 500 L900 500" />
            <text x="885" y="540" fontFamily="JetBrains Mono, monospace" fontSize="9" fill={dimmed} transform="rotate(-90 890 540)">
              ENTRANCE
            </text>
          </g>
        </g>

        {/* Corner label */}
        <g>
          <text x="72" y="600" fontFamily="JetBrains Mono, monospace" fontSize="10" fill={dimmed}>
            grocera · store id 04 · live
          </text>
        </g>
      </svg>
    </div>
  );
};

/**
 * HowItWorks — scroll-pinned section (Meuze pattern).
 *  - The section spans 4x viewport tall so scroll drives progression through
 *    4 tabs (Demand, Labor, Inventory, Locations).
 *  - Inner content is sticky so it stays pinned while the section scrolls.
 *  - Active tab expands with description; others collapse to title only.
 *  - A vertical dot indicator tracks scroll progress inside the section.
 */
export default function HowItWorks() {
  const sectionRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // Progress starts when section top hits viewport top; ends when bottom leaves
      const total = rect.height - vh;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      const p = total > 0 ? scrolled / total : 0;
      setProgress(p);
      const idx = Math.min(howItWorksTabs.length - 1, Math.floor(p * howItWorksTabs.length));
      setActive(idx);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const activeKey = howItWorksTabs[active].key;
  // Remap procurement tab keys to illustration highlight zones so the store visual still lights up logically.
  const activeHighlight = {
    orders: 'demand',
    suppliers: 'locations',
    exceptions: 'labor',
    contracts: 'inventory',
  }[activeKey] || 'demand';

  return (
    <>
    <section
      id="how-it-works"
      ref={sectionRef}
      className="relative text-white"
      // 4 viewports tall — enough scroll room for 4 tab states
      style={{ height: '380vh' }}
    >
      {/* pt-16 = the fixed navbar's height, so the pinned panel centers below it */}
      <div className="sticky top-0 h-screen pt-16 flex flex-col justify-center">
        <div className="mx-auto max-w-[1400px] px-6 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-16 items-start mb-6 md:mb-10 [@media(max-height:780px)]:mb-4">
            <div className="lg:col-span-6">
              <p className="eyebrow mb-4">How it works</p>
              <h2 className="h-display text-[34px] md:text-[56px] [@media(max-height:780px)]:md:text-[44px] leading-[1.02] text-white max-w-[560px]">
                Learn your retail, run it end-to-end.
              </h2>
            </div>
            <div className="hidden md:block lg:col-span-6 lg:pt-6">
              <p className="text-white/60 text-[14.5px] leading-[1.65] max-w-[520px]">
                Grocera connects to your POS, inventory, and supplier systems. It learns your store patterns —
                traffic, seasonal demand, supplier reliability — then automates ordering, stock optimization,
                and multi-location replenishment 24/7, with full transparency.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 relative">
            {/* Scroll dot indicator */}
            <div className="hidden lg:block absolute left-0 top-0 bottom-0 w-[2px]">
              <div className="absolute left-0 top-0 bottom-0 w-px bg-white/10" />
              <div
                className="absolute left-[-3px] w-2 h-2 bg-[#2E6BFF]"
                style={{
                  top: `${progress * 100}%`,
                  transform: 'translateY(-50%)',
                  transition: 'top 120ms linear',
                  boxShadow: '0 0 12px #2E6BFF',
                }}
              />
            </div>

            <div className="lg:col-span-5 lg:pl-6">
              <div className="flex flex-col gap-2 md:gap-4">
                {howItWorksTabs.map((t, i) => {
                  const isActive = active === i;
                  return (
                    <div
                      key={t.key}
                      className="tab-link"
                      onClick={() => {
                        // Scroll user to this tab's segment
                        const el = sectionRef.current;
                        if (!el) return;
                        const rect = el.getBoundingClientRect();
                        const total = rect.height - window.innerHeight;
                        const target = window.scrollY + rect.top + (i + 0.5) / howItWorksTabs.length * total;
                        window.scrollTo({ top: target, behavior: 'smooth' });
                      }}
                    >
                      <div
                        className={`text-[22px] h-sans transition-colors ${
                          isActive ? 'text-[#2E6BFF]' : 'text-white'
                        }`}
                      >
                        {t.label}
                      </div>
                      <div
                        style={{
                          maxHeight: isActive ? '120px' : '0px',
                          opacity: isActive ? 1 : 0,
                          overflow: 'hidden',
                          transition: 'max-height 400ms ease, opacity 300ms ease, margin 300ms ease',
                          marginTop: isActive ? '8px' : '0px',
                        }}
                      >
                        <p className="text-white/60 text-[14px] leading-[1.6] max-w-[420px]">
                          {t.body}
                        </p>
                      </div>
                      <div className="divider mt-4" />
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Decorative; dropped on short phones so the pinned panel still fits one screen. */}
            <div className="lg:col-span-7 w-full max-w-[520px] mx-auto lg:max-w-none max-lg:[@media(max-height:700px)]:hidden">
              <IsometricStore activeKey={activeHighlight} />
            </div>
          </div>

        </div>
      </div>
    </section>

    {/* Demo video — its own block after the pinned section. Inside the
        h-screen sticky panel it had no room and overflowed past the
        viewport into the next section. */}
    <div className="mx-auto max-w-[1100px] px-6 pt-16 pb-24 md:pt-24 md:pb-32 text-white">
      <p className="eyebrow mb-4">Demo</p>
      <h3 className="h-display text-[32px] md:text-[44px] leading-[1.05] mb-8 md:mb-10">See it in action</h3>
      <div className="relative w-full aspect-video overflow-hidden rounded-lg border border-white/10 bg-black">
        <iframe
          src="https://www.youtube.com/embed/h227a6KzZfU"
          title="Grocera AI Demo"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
          className="absolute inset-0 w-full h-full"
        />
      </div>
    </div>
    </>
  );
}
